import { createContext, type JSX, useContext } from 'solid-js'
import {
  AdminEventsApiDataSource,
  type BackendEvent,
  type AttendeeRecord,
  type BulkValidationResult,
  type BulkCreationResult,
  type PresaleValidationResult,
  type CreateEventPayload,
  type UpdateEventPayload,
  type PresaleDashboard,
  type PresaleCodePoolWithEvents,
  type PresalePoolDates
} from '../../../features/events/data/api/admin-events.api-datasource'
import axios from '../../services/axios.interceptor'
import { organizationSlug } from '../user-provider'

interface AdminEventsContextType {
  getEvents: () => Promise<BackendEvent[]>
  getEventById: (eventId: number) => Promise<BackendEvent | null>
  getRsvpList: (eventId: number) => Promise<AttendeeRecord[]>
  getMeetAndGreetList: (eventId: number) => Promise<AttendeeRecord[]>
  createEvent: (payload: CreateEventPayload) => Promise<BackendEvent>
  updateEvent: (eventId: number, payload: UpdateEventPayload) => Promise<BackendEvent>
  deleteEvent: (eventId: number) => Promise<void>
  uploadEventImage: (eventId: number, file: File) => Promise<{ fileId: number; imageUrl: string }>
  downloadBulkTemplate: (format?: 'csv' | 'xlsx') => Promise<void>
  validateBulkEvents: (file: File) => Promise<BulkValidationResult>
  createBulkEvents: (file: File) => Promise<BulkCreationResult>
  validatePresaleCodes: (file: File, name: string) => Promise<PresaleValidationResult>
  createPresalePool: (file: File, name: string, dates?: PresalePoolDates) => Promise<{ poolId: number }>
  assignPoolToEvent: (
    poolId: number,
    eventId: number,
    dates?: { presaleStart?: string; presaleEnd?: string; generalSaleStart?: string }
  ) => Promise<void>
  unassignPoolFromEvent: (poolId: number, eventId: number) => Promise<void>
  updatePresalePoolDates: (poolId: number, dates: PresalePoolDates) => Promise<void>
  getEventCategories: () => Promise<{ id: number; name: string }[]>
  getPresaleCodePools: () => Promise<PresaleCodePoolWithEvents[]>
  getPresaleDashboard: () => Promise<PresaleDashboard | null>
  exportMembersPresaleCodes: () => Promise<void>
}

const AdminEventsContext = createContext<AdminEventsContextType>()

export const CorisAdminEventsProvider = (props: { children: JSX.Element }): JSX.Element => {
  const dataSource = new AdminEventsApiDataSource(import.meta.env.VITE_BACKEND_V2_URL, axios, organizationSlug)

  const value: AdminEventsContextType = {
    getEvents: () => dataSource.getEvents(),
    getEventById: (eventId: number) => dataSource.getEventById(eventId),
    getRsvpList: (eventId: number) => dataSource.getRsvpList(eventId),
    getMeetAndGreetList: (eventId: number) => dataSource.getMeetAndGreetList(eventId),
    createEvent: (payload: CreateEventPayload) => dataSource.createEvent(payload),
    updateEvent: (eventId: number, payload: UpdateEventPayload) => dataSource.updateEvent(eventId, payload),
    deleteEvent: (eventId: number) => dataSource.deleteEvent(eventId),
    uploadEventImage: (eventId: number, file: File) => dataSource.uploadEventImage(eventId, file),
    downloadBulkTemplate: (format?: 'csv' | 'xlsx') => dataSource.downloadBulkTemplate(format),
    validateBulkEvents: (file: File) => dataSource.validateBulkEvents(file),
    createBulkEvents: (file: File) => dataSource.createBulkEvents(file),
    validatePresaleCodes: (file: File, name: string) => dataSource.validatePresaleCodes(file, name),
    createPresalePool: (file: File, name: string, dates?) => dataSource.createPresalePool(file, name, dates),
    assignPoolToEvent: (poolId: number, eventId: number, dates?) =>
      dataSource.assignPoolToEvent(poolId, eventId, dates),
    unassignPoolFromEvent: (poolId: number, eventId: number) => dataSource.unassignPoolFromEvent(poolId, eventId),
    updatePresalePoolDates: (poolId: number, dates) => dataSource.updatePresalePoolDates(poolId, dates),
    getEventCategories: () => dataSource.getEventCategories(),
    getPresaleCodePools: () => dataSource.getPresaleCodePools(),
    getPresaleDashboard: () => dataSource.getPresaleDashboard(),
    exportMembersPresaleCodes: () => dataSource.exportMembersPresaleCodes()
  }

  return <AdminEventsContext.Provider value={value}>{props.children}</AdminEventsContext.Provider>
}

export const useAdminEvents = (): AdminEventsContextType => {
  const context = useContext(AdminEventsContext)
  if (!context) {
    throw new Error('useAdminEvents must be used inside a CorisAdminEventsProvider')
  }
  return context
}
